import CustomerResponse from "../../models/Customer/CustomerResponse";
import Tenancies from "../../models/Customer/Tenancies";
import { isNullOrUndefined } from "../formatting";

export class Helper {
  static readonly PreAssessmentCategoryPrefix = "PREASSESS";
  static readonly PreAssessmentEligibleAssetTypes: ReadonlyArray<string> = [
    "Property",
    "Communal",
    "Block",
    "Estate",
    "Divided Block",
    "Divided block",
    "Estate Area",
  ];
  static readonly EmaFullResponsibilityCode = "EMAFULL";
  static readonly Section8SupplementaryCode = "LHLDSECTION8";

  static normalizePreAssessmentCategory(category?: string | null): string {
    return (category || "").trim().toUpperCase();
  }

  static isPreAssessmentCategory(category?: string | null): boolean {
    return Helper.normalizePreAssessmentCategory(category).startsWith(
      `${Helper.PreAssessmentCategoryPrefix}-`
    );
  }

  static isMatchingPreAssessmentCategory(
    category?: string | null,
    expectedCategory?: string | null
  ): boolean {
    const normalizedCategory = Helper.normalizePreAssessmentCategory(category);
    const normalizedExpectedCategory = Helper.normalizePreAssessmentCategory(expectedCategory);

    return Boolean(normalizedCategory && normalizedExpectedCategory) &&
      normalizedCategory === normalizedExpectedCategory;
  }

  static ValidationRegex = {
    NumericOnly: /^[+0-9]+$/, // Regex for numeric input (phone number)
    AlphabetWithSpecialChars: /^[-A-Za-z' \b]+$/, // Regex for name input
    All_Special_Chars_Allowed: /^[A-Za-z0-9\s|.,_@+#%'\-()^`~=¬£€\\/[\]?:;"{}]*$/,//All Special Chars Allowed - .()%@:?@#%:"'',./?|]{}~`-=+
  };

  static LengthValidation = {
    CrimeReferenceNumber: {
      Minimum: 3,
      Maximum: 20,
    },
  };

  static validateMinLength(
    t: (key: string, params?: Record<string, any>) => string,
    value: string,
    fieldName: string,
    minLength: number
  ): string | null {
    if (value.length < minLength) {
      return t("minimum_length", {
        field: fieldName,
        min: minLength,
      });
    }
    return null;
  }

  static validateMaxLength(
    t: (key: string, params?: Record<string, any>) => string,
    value: string,
    fieldName: string,
    maxLength: number
  ): string | null {
    if (value.length > maxLength) {
      return t("maximum_length", {
        field: fieldName,
        max: maxLength,
      });
    }
    return null;
  }

  static validateInvalidCharacters(
    t: (key: string, params?: Record<string, any>) => string,
    value: string,
    fieldName: string,
    regex: RegExp
  ): string | null {
    if (!regex.test(value)) {
      return t("invalid_characters", {
        field: fieldName,
      });
    }
    return null;
  }

  static readonly HHSRSRepairCauseId = 14;

  static readonly RepairCauseLabels = {
    1: "General Wear and Tear",
    2: "Complaint",
    3: "Vandalism or Damage",
    4: "Technical Inspection",
    5: "Disrepair",
    6: "Right to Improve",
    7: "Mutual Exchange",
    8: "Leaseholder Improvement Request",
    9: "Estate Management Survey",
    10: "Keysafe Installation",
    11: "Void Survey",
    12: "Damp and Mould Survey",
    14: "Housing Health and Safety Rating System"
  } as const;

  static sortTenanciesByLatestResidentialStatus(tenancyResponse: Tenancies[], customerResponse: CustomerResponse): Tenancies[] {
    let sortedTenancyResponse: Tenancies[] = [];
    //sort tenancyResponse by IsTenancyActive descending and then by IsResidential descending and then by tenancyStartDate in desceding order using customerResponse
    //hanldle Isresidential flag AS Null
    try {
      if (tenancyResponse && tenancyResponse.length > 0) {
        if (isNullOrUndefined(customerResponse?.tenanciesSummaries[0]?.tenancySummary?.isResidential)) {
          return tenancyResponse;
        }
        sortedTenancyResponse = tenancyResponse.sort((a, b) => {
          const aIsActive = customerResponse?.tenanciesSummaries.find(
            (ts) => ts.tenancySummary.tenancyId === a.tenancyIdentifier
          )?.tenancySummary.statuses.find(
            (s) => s.type === "isTenancyActive"
          )?.value;
          const bIsActive = customerResponse?.tenanciesSummaries.find(
            (ts) => ts.tenancySummary.tenancyId === b.tenancyIdentifier
          )?.tenancySummary.statuses.find(
            (s) => s.type === "isTenancyActive"
          )?.value;

          const aIsResidential = customerResponse?.tenanciesSummaries.find(
            (ts) => ts.tenancySummary.tenancyId === a.tenancyIdentifier
          )?.tenancySummary.isResidential;
          const bIsResidential = customerResponse?.tenanciesSummaries.find(
            (ts) => ts.tenancySummary.tenancyId === b.tenancyIdentifier
          )?.tenancySummary.isResidential;

          if (aIsActive !== bIsActive) {
            return aIsActive ? 1 : -1; // Active tenancies first
          }
          if (aIsResidential !== bIsResidential) {
            return aIsResidential ? -1 : 1; // Residential tenancies next
          }
          // Sort by tenancy start date if both are active and residential
          const aStartDate = new Date(
            customerResponse?.tenanciesSummaries.find(
              (ts) => ts.tenancySummary.tenancyId === a.tenancyIdentifier
            )?.tenancySummary.tenancyStartDate || 0
          );
          const bStartDate = new Date(
            customerResponse?.tenanciesSummaries.find(
              (ts) => ts.tenancySummary.tenancyId === b.tenancyIdentifier
            )?.tenancySummary.tenancyStartDate || 0
          );
          return bStartDate.getTime() - aStartDate.getTime();
        });
      }
      else {
        return tenancyResponse; // Return original array if no tenancies found
      }
    } catch (error) {
      return tenancyResponse; // Return original array if sorting fails
    }
    return sortedTenancyResponse;
  }
}